import { defineCollection } from "@rebasepro/cms-types";
import { policy } from "@rebasepro/types";
import customersCollection from "./customers.js";
import tiersCollection from "./tiers.js";
import transactionsCollection from "./transactions.js";

const tierChangesCollection = defineCollection({
    name: "Tier Changes",
    singularName: "Tier Change",
    slug: "tier_changes",
    table: "tier_changes",
    description: "Audit log of every tier upgrade or downgrade. Provides full traceability for anti-fraud and customer service.",
    securityRules: [
        { operation: "select", access: "public" },
        { operations: ["insert", "update", "delete"], roles: ["admin"] },
        // Ver la nota en transactions.ts: el motor de niveles registra aqui
        // cada cambio de nivel, tambien sin usuario autenticado.
        { operations: ["insert", "update", "delete"], condition: policy.serverContext() }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid",
            admin: { readOnly: true }
        },
        customer: {
            name: "Customer",
            type: "relation",
            validation: { required: true },
            relation: {
                kind: "belongsTo",
                target: () => customersCollection,
                // 0.18 made a required belongsTo default to ON DELETE RESTRICT.
                // The live database, the migration baseline and
                // tests/foreign-key-parity.test.ts all have CASCADE here, and a
                // boot-time ensure would rewrite the constraint in production on
                // the first deploy of that default. Written down so nothing moves;
                // whether the ledger should refuse a customer delete is a
                // decision to take on purpose, with a migration, not by omission.
                onDelete: "cascade",
            }
        },
        previous_tier: {
            name: "Previous Tier",
            type: "relation",
            description: "Tier before the change (null for first assignment)",
            relation: {
                kind: "belongsTo",
                target: () => tiersCollection,
            }
        },
        new_tier: {
            name: "New Tier",
            type: "relation",
            description: "Tier after the change (null when the customer is revoked below the entry tier by an anti-fraud return)",
            relation: {
                kind: "belongsTo",
                target: () => tiersCollection,
            }
        },
        reason: {
            name: "Reason",
            type: "string",
            validation: { required: true },
            enum: [
                { id: "purchase", label: "Purchase", color: "green" },
                { id: "return", label: "Return (Anti-Fraud)", color: "red" },
                { id: "manual_override", label: "Manual Override", color: "orange" },
                { id: "expiry", label: "12 months ended", color: "gray" }
            ]
        },
        net_points_at_change: {
            name: "Net Points at Change",
            type: "number",
            description: "Snapshot of the customer's net points when the tier change occurred",
            admin: { readOnly: true }
        },
        triggered_by_transaction: {
            name: "Triggered By Transaction",
            type: "relation",
            description: "The transaction that caused this tier change (null for manual overrides)",
            relation: {
                kind: "belongsTo",
                target: () => transactionsCollection,
            }
        },
        created_at: {
            name: "Created At",
            type: "date",
            mode: "date_time",
            autoValue: "on_create",
            admin: { readOnly: true }
        }
    },
    admin: {
        icon: "ArrowUpDown",
        group: "Loyalty",
        defaultViewMode: "list",
        enabledViews: ["list", "table"],
        // The points log reads as a sentence across the row: who, from which tier
        // to which, why, and at how many points.
        listProperties: ["previous_tier", "new_tier", "reason", "net_points_at_change"],
        previewProperties: ["customer"],
        openEntityMode: "side_panel",
        exportable: true,
        sort: ["created_at", "desc"],
        propertiesOrder: [
            "customer", "previous_tier", "new_tier",
            "reason", "net_points_at_change",
            "triggered_by_transaction", "created_at"
        ]
    }
});

export default tierChangesCollection;
